import { Job } from '@betterleap/client';
import {
  BoundInput,
  Button,
  ComboboxField,
  Editor,
  Form,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Option,
  PromiseModal,
  showToast,
  Spinner,
} from '@betterleap/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { capitalize, startCase } from 'lodash';
import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { z } from 'zod';
import { convertEnumKeyToUIString } from '../../../helper/convertEnumKeyToUIString';
import {
  ComboOption,
  mapEnumToOptions,
} from '../../../helper/mapEnumToOptions';
import { getApiClient } from '../../../lib';
import {
  AutocompleteOption,
  useAutocompleteOptions,
} from '../../../hooks/useAutocomplete';

const urlPattern = /^(https?:\/\/[^\s$.?#].[^\s]*)$/;

// Custom validator for numbers and empty strings that are converted to null instead of 0.
const ZodStringNumberOrNull = z
  .string()
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .refine((value) => value === null || !isNaN(Number(value)), {
    message: 'Invalid number',
  })
  .transform((value) => (value === null ? null : Number(value)));

const jobSchema = z
  .object({
    title: z.string().min(1, 'Title is required'),
    locationRaw: z.string().min(1, 'Location is required'),
    fromPay: ZodStringNumberOrNull,
    toPay: ZodStringNumberOrNull,
    employmentType: z.nativeEnum(Job.employmentType),
    locationType: z.nativeEnum(Job.locationType),
    currency: z.nativeEnum(Job.currency),
    payFrequency: z.nativeEnum(Job.payFrequency),
    applyLink: z
      .string()
      .nullable()
      .refine(
        (val: string | null) =>
          val === null || val === '' || urlPattern.test(val),
        {
          message: 'Apply Link must be a valid URL',
        },
      ),
    description: z.string().min(1, 'Description is required'),
    published: z.boolean().default(false),
    locality: z.string(),
    region: z.string(),
    country: z.string(),
    postalCode: z.string(),
  })
  .refine(
    (data) => {
      const fromPayValid =
        typeof data.fromPay === 'number' && !isNaN(data.fromPay);
      const toPayValid = typeof data.toPay === 'number' && !isNaN(data.toPay);

      if (!fromPayValid || !toPayValid || !data.toPay || !data.fromPay)
        return true;

      return data.toPay >= data.fromPay;
    },
    {
      path: ['toPay'],
      message: 'To salary cannot be less than From.',
    },
  );

type JobFormData = z.infer<typeof jobSchema>;

export const DollarSign = () => (
  <div className='relative w-5'>
    <span className='absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500'>
      $
    </span>
  </div>
);

export const JobForm: PromiseModal<{
  initialJob: Job;
}> = ({ initialJob = {} as Job, onDismiss, onSubmit }) => {
  const apiClient = getApiClient();
  const isPublishingRef = useRef(false);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<JobFormData>({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      title: initialJob.title || '',
      locationRaw: initialJob.locationRaw || '',
      fromPay: initialJob.fromPay || null,
      toPay: initialJob.toPay || null,
      employmentType: initialJob.employmentType || Job.employmentType.FULL_TIME,
      locationType: initialJob.locationType || Job.locationType.ONSITE,
      currency: initialJob.currency || Job.currency.USD,
      payFrequency: initialJob.payFrequency || Job.payFrequency.ANNUALLY,
      applyLink: initialJob.applyLink || null,
      description: initialJob.description || '',
      published: initialJob.published || false,
      locality: initialJob.locality || '',
      region: initialJob.region || '',
      country: initialJob.country || '',
      postalCode: initialJob.postalCode || '',
    },
  });

  const employmentType = watch('employmentType');
  const locationType = watch('locationType');
  const description = watch('description');
  const location = watch('locationRaw');
  const title = watch('title');

  const {
    setSearchTerm: setLocationSearchTerm,
    options: locationOptions,
    isLoading: isLocationOptionsLoading,
  } = useAutocompleteOptions({
    field: 'localities',
  });

  const { mutate: saveJob, isLoading: isSaving } = useMutation(
    async (job: JobFormData) => {
      if (!initialJob.id) {
        return apiClient.jobsBoard.createJob({
          requestBody: job,
        });
      } else {
        return apiClient.jobsBoard.updateJob({
          id: initialJob.id,
          requestBody: job,
        });
      }
    },
    {
      onSuccess: ({ data: job }) => {
        showToast({
          variant: 'success',
          title: 'Success',
          description: `Job successfully ${
            !initialJob.id ? 'created' : 'updated'
          } on Betterleap`,
        });

        onSubmit({
          job,
        });
      },
      onError: () => {
        showToast({
          variant: 'danger',
          title: 'Error',
          description: `Failed to ${
            !initialJob.id ? 'create' : 'update'
          } job. Please try again.`,
        });
      },
    },
  );

  const handleSaveAsDraft = (data: JobFormData) => {
    saveJob({ ...data, published: false });
  };

  const handleSaveAndPublish = (data: JobFormData) => {
    saveJob({ ...data, published: true });
  };

  const onFormSubmit = (data: JobFormData) => {
    if (data.applyLink === '') {
      data.applyLink = null;
    }
    if (isPublishingRef.current) {
      handleSaveAndPublish(data);
    } else {
      handleSaveAsDraft(data);
    }
  };

  return (
    <Modal
      containFocus={false}
      size={{ '@initial': 'full', '@md': 'xxl' }}
      isDismissable={false}
    >
      <ModalHeader>
        <ModalTitle>{initialJob.id ? 'Edit' : 'Add'} Job</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Form
          control={control}
          onSubmit={handleSubmit(onFormSubmit)}
          className='flex flex-col h-full'
        >
          <div className='grid gap-x-2 grid-cols-1 md:grid-cols-2 px-1'>
            <BoundInput
              id='title'
              name='title'
              label='Job title'
              value={title}
              placeholder='Create job title'
              required
              error={errors.title?.message}
            />
            <ComboboxField
              id='employmentType'
              label='Employment type'
              value={employmentType ? [employmentType] : []}
              placeholder='Select employment type'
              error={errors.employmentType?.message}
              options={mapEnumToOptions(Job.employmentType)}
              clearable={false}
              onChange={(_keys, selected) =>
                setValue('employmentType', selected[0].id)
              }
              renderSelection={() => {
                return <>{convertEnumKeyToUIString(employmentType)}</>;
              }}
              required
            >
              {(option: ComboOption) => (
                <Option>{capitalize(option.text)}</Option>
              )}
            </ComboboxField>

            <ComboboxField
              id='locality'
              label='Employee location'
              loading={isLocationOptionsLoading}
              value={location ? [location] : []}
              placeholder='Select employee location'
              error={errors.locationRaw?.message}
              leftIcon={<Icon name='search' color='$neutral-blue-700' />}
              renderSelection={() => {
                return <>{startCase(location ?? '')}</>;
              }}
              options={locationOptions}
              onChange={(_keys, selected) => {
                const newLocation = selected?.[0]?.text || '';
                const parts = newLocation.split(',');
                const city = parts[0];
                const state = parts[1];
                const country = parts[2];
                setValue('locationRaw', newLocation);
                setValue('locality', city);
                setValue('region', state);
                setValue('country', country);
              }}
              onFilterChange={setLocationSearchTerm}
              required
            >
              {(option: AutocompleteOption) => <Option>{option.text}</Option>}
            </ComboboxField>

            <ComboboxField
              id='locationType'
              label='Workplace type'
              value={locationType ? [locationType] : []}
              placeholder='Select workplace type'
              options={mapEnumToOptions(Job.locationType)}
              error={errors.locationType?.message}
              clearable={false}
              onChange={(_keys, selected) =>
                setValue('locationType', selected[0].id)
              }
              renderSelection={() => {
                return <>{capitalize(locationType)}</>;
              }}
              required
            >
              {(option: ComboOption) => (
                <Option>{capitalize(option.text)}</Option>
              )}
            </ComboboxField>

            <div className='flex gap-x-2 items-center justify-center'>
              <BoundInput
                id='fromPay'
                name='fromPay'
                label='Salary range (from)'
                type='number'
                leftIcon={<DollarSign />}
                placeholder='Enter minimum salary'
                className='flex-1'
                error={errors.fromPay?.message}
              />

              <span className='flex items-center text-gray-500 pt-[7px]'>
                -
              </span>

              <BoundInput
                id='toPay'
                name='toPay'
                label='Salary range (to)'
                type='number'
                leftIcon={<DollarSign />}
                placeholder='Enter maximum salary'
                className='flex-1'
                error={errors.toPay?.message}
              />
            </div>

            <BoundInput
              id='applyLink'
              name='applyLink'
              label='Application link'
              placeholder='https://...'
              error={errors.applyLink?.message}
            />
          </div>

          <div className='px-2 flex flex-col flex-1 overflow-auto'>
            <p className='text-gray-500 font-normal text-sm mb-2'>
              Job description
            </p>
            <Editor
              value={description}
              onChange={(value) => setValue('description', value)}
              invalid={!!errors.description}
              css={{
                height: '100%',
                borderRadius: '$xl',
                marginTop: '$2',
                overflow: 'auto',
                '& .quill': {
                  height: '100%',
                },
                '& .ql-toolbar': {
                  borderTopLeftRadius: '0.75rem',
                  borderTopRightRadius: '0.75rem',
                },
                '& .ql-container.ql-snow': {
                  height: 'calc(100% - 50px)',
                  borderBottomLeftRadius: '0.75rem',
                  borderBottomRightRadius: '0.75rem',
                  overflow: 'auto',
                },
                '& .ql-editor': {
                  minHeight: 156,
                  height: 'auto',
                },
              }}
            />
          </div>

          <ModalFooter className='flex justify-between pt-4 px-3'>
            {!initialJob.published && (
              <Button
                type='submit'
                variant='link'
                onClick={() => {
                  isPublishingRef.current = false;
                }}
              >
                Save as draft
              </Button>
            )}

            <div className='ml-auto space-x-4 flex'>
              <Button variant='gray' onClick={onDismiss}>
                Cancel
              </Button>
              <Button
                type='submit'
                onClick={() => {
                  isPublishingRef.current = true;
                }}
              >
                {isSaving ? <Spinner variant='white' /> : 'Save & Publish'}
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </ModalBody>
    </Modal>
  );
};
